import type { AgencyListTab } from '@saaga/shared/interfaces/agencies.interface'
import { BookOpenTextIcon, BuildingIcon, FileEditIcon, StoreIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Tabs, TabsList } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

import { AgencyTabItem } from './agency-tab-item.atom'

interface AgenciesPageHeaderProps {
  activeTab: AgencyListTab
  onTabChange: (tab: AgencyListTab) => void
  disabledTabList?: AgencyListTab[]
  onUpgradeClick?: () => void
  className?: string
}

interface TabConfig {
  value: AgencyListTab
  label: string
  icon: typeof StoreIcon
  title: string
  description: string
}

const tabConfigList: TabConfig[] = [
  {
    value: 'explore',
    label: 'Marketplace',
    icon: StoreIcon,
    title: 'Agency Marketplace',
    description:
      'Discover agencies published by other authors. Browse and use pre-built AI agencies for various tasks.',
  },
  {
    value: 'organization',
    label: 'Organization',
    icon: BuildingIcon,
    title: 'Organization Agencies',
    description:
      'Agencies published to your organization. These are visible to all team members and ready to use.',
  },
  {
    value: 'studio',
    label: 'Studio',
    icon: FileEditIcon,
    title: 'Agency Studio',
    description:
      'Your work-in-progress agencies. Create, edit, and publish agencies to your organization from here.',
  },
  {
    value: 'templates',
    label: 'My Prompt Templates',
    icon: BookOpenTextIcon,
    title: 'My Prompt Templates',
    description:
      'Manage your personal prompt templates. Create reusable prompts to streamline your conversations.',
  },
]

export const AgenciesPageHeader = ({
  activeTab,
  onTabChange,
  disabledTabList = [],
  onUpgradeClick,
  className,
}: AgenciesPageHeaderProps): ReactNode => {
  const activeConfig =
    tabConfigList.find((config) => config.value === activeTab) ?? tabConfigList[0]

  const handleTabChange = (value: string): void => {
    onTabChange(value as AgencyListTab)
  }

  return (
    <div className={cn('agencies-header', className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h1 className="agencies-header-title text-2xl font-bold tracking-tight">
            {activeConfig.title}
          </h1>
          <p className="agencies-header-description text-sm text-muted-foreground mt-1 max-w-2xl">
            {activeConfig.description}
          </p>
        </div>
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="agencies-tabs">
            {tabConfigList.map((config) => (
              <AgencyTabItem
                key={config.value}
                value={config.value}
                label={config.label}
                icon={config.icon}
                isDisabled={disabledTabList.includes(config.value)}
                onUpgradeClick={onUpgradeClick}
              />
            ))}
          </TabsList>
        </Tabs>
      </div>
    </div>
  )
}
